import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  DAY_SCOPE_QUERY_KEY,
  clinicToday,
  createDayScopeGate,
  isClinicDate,
  readDayParam,
  resolveDayScope,
  stepClinicDate,
  writeDayParam,
  type DayScopeGate,
  type DayScopeKind,
} from './day-scope';

/**
 * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 -- the day-scope STATE hook.
 *
 * The React half of the primitive: it binds the selected clinic day to the
 * URL using the repo's existing routing pattern (react-router
 * `useSearchParams`, the same idiom the leads workspace, the owner reports
 * and the call-center workspace already use) and hands the container the
 * three moves the DATE COMMAND BAR fires.
 *
 * STATE LAW (A3), all of it here so no adopting surface re-implements any of
 * it:
 *  - the day lives in the query string, so a REFRESH restores it;
 *  - a day change PUSHES a history entry, so browser back / forward walks the
 *    date history honestly (react-router's default; `replace` is never used
 *    for a day move);
 *  - today is the canonical bare URL (the key is removed), so "one-click
 *    Today" always lands on ONE address;
 *  - a malformed `?date` resolves to today and is FLAGGED (never a guessed
 *    date; the server independently 400s a malformed value it is sent);
 *  - a deep-linked day passes the SAME role / org fences as normal
 *    navigation -- the day is a parameter to the same fenced read, and
 *    nothing here touches authorization;
 *  - the URL carries the day and nothing else: no identity, no patient, no
 *    clinical token, so "no PHI beyond the date" holds by construction.
 *
 * The stale-response gate (A7) is created ONCE per surface and returned, so a
 * container can mark each read with its request identity and discard any
 * response that is no longer the latest.
 */
export interface DayScopeState {
  /** The selected clinic day, 'YYYY-MM-DD'. */
  readonly date: string;
  /** The clinic's today for this render pass. */
  readonly today: string;
  readonly scope: DayScopeKind;
  /** True when the URL carried a value that is not a clinic date. */
  readonly malformed: boolean;
  /** Select an arbitrary day (a non-date value is ignored, never guessed). */
  readonly selectDate: (date: string) => void;
  /** Move exactly one clinic day. */
  readonly stepDay: (deltaDays: number) => void;
  /** Jump to the clinic's today (also clears a stale key). */
  readonly goToday: () => void;
  /** The per-surface request-identity gate (stable across renders). */
  readonly gate: DayScopeGate;
}

export function useDayScope(options: { readonly now?: Date } = {}): DayScopeState {
  const [searchParams, setSearchParams] = useSearchParams();
  // The clinic's today for this render. `now` is an injected test seam; the
  // default reads the clock exactly once per render pass, and every derived
  // value below is a pure function of it.
  const today = clinicToday(options.now ?? new Date());
  const raw = searchParams.get(DAY_SCOPE_QUERY_KEY);
  const resolution = readDayParam(raw, today);
  const date = resolution.date;

  // ONE gate per surface, held across renders.
  const gateRef = useRef<DayScopeGate | null>(null);
  if (gateRef.current === null) gateRef.current = createDayScopeGate();
  const gate = gateRef.current;

  const applyDate = useCallback(
    (next: string) => {
      // Pushes (never replaces): the date history must be walkable.
      setSearchParams((current) =>
        writeDayParam([...current.entries()], next, clinicToday(options.now ?? new Date())),
      );
    },
    [setSearchParams, options.now],
  );

  const selectDate = useCallback(
    (next: string) => {
      // A cleared picker or an impossible day is IGNORED -- the bar never
      // invents a date and the surface never fetches a day nobody chose.
      if (!isClinicDate(next.trim())) return;
      applyDate(next.trim());
    },
    [applyDate],
  );

  const stepDay = useCallback(
    (deltaDays: number) => {
      applyDate(stepClinicDate(date, deltaDays));
    },
    [applyDate, date],
  );

  const goToday = useCallback(() => {
    applyDate(clinicToday(options.now ?? new Date()));
  }, [applyDate, options.now]);

  const scope = useMemo(() => resolveDayScope(date, today), [date, today]);

  return {
    date,
    today,
    scope,
    malformed: resolution.malformed,
    selectDate,
    stepDay,
    goToday,
    gate,
  };
}

export default useDayScope;
